import {
	ContractSuretyType,
	FinalContractSuretyBondFormType,
	formatToDollar,
	ISODate,
	ISODateTime,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { SuretyAccountPreview } from "@portal/api/account/agent";
import { SuretyBondPreview } from "@portal/api/bond/agent";
import { BondRequestPreview } from "@portal/api/bond-request/agent";
import { DateTime } from "luxon";
import { match } from "ts-pattern";

export const contractSuretyTypeToString = (contractSuretyType: ContractSuretyType): string => {
	return match(contractSuretyType)
		.with("bid", () => "Bid")
		.with("bid_to_final", () => "Bid To Final")
		.with("final", () => "Final")
		.otherwise(() => "--");
};

export const finalBondFormTypeToString = (
	bondType: Option<FinalContractSuretyBondFormType>,
): Option<string> => {
	return Option.map(bondType, (type) =>
		match(type)
			.with("performance", () => "Performance")
			.with("payment", () => "Payment")
			.with("pnp", () => "Performance and Payment")
			.otherwise(() => type),
	);
};

/**
 * A $0 bond amount means the amount is still being extracted or extraction failed,
 * so it's shown as the empty placeholder rather than as a real value.
 */
export const formatBondAmount = (amount: number | null | undefined) =>
	formatEmpty(amount ? formatToDollar(amount) : null);

/**
 * displayTitle is a "$<amount> <Type> Bond" to "-- <Type> Bond" swap
 * when the leading amount is $0, matching formatBondAmount.
 */
export const formatDisplayTitle = (displayTitle: string) =>
	displayTitle.replace(/^\$0(?=\s)/, "--");

export const formatSuretyBondDescription = (
	bond?: SuretyBondPreview,
	useHyphen: boolean = false,
) => {
	if (!bond) {
		console.error("formatSuretyBondDescription called with undefined bond");
		return "Unknown Bond";
	}

	const contractTypeString = contractSuretyTypeToString(bond.type as ContractSuretyType);
	return `${formatToDollar(bond.amount)} ${contractTypeString} Bond ${useHyphen ? "-" : "for"} ${bond.principalName}`;
};

export const formatSuretyAccountDescription = (account?: SuretyAccountPreview) => {
	if (!account) {
		console.error("formatSuretyAccountDescription called with undefined account");
		return "Unknown Account";
	}

	const primaryCompany = account.companies.find(
		(company) => company.id === account.primaryCompanyId,
	);

	// Fallback to display name if primary company is not found or principal name is the same
	if (!primaryCompany || account.displayName === primaryCompany.name) {
		return account.displayName;
	}

	return `${account.displayName} (${primaryCompany.name})`;
};

export const formatSuretyQuoteDescription = (quote?: BondRequestPreview, omitPrincipal = false) => {
	if (!quote) {
		console.error("formatSuretyQuoteDescription called with undefined quote");
		return "Unknown Quote";
	}

	const contractTypeString = contractSuretyTypeToString(quote.suretySubType as ContractSuretyType);
	return `${formatBondAmount(quote.amount)} ${contractTypeString} Bond Request${omitPrincipal ? "" : ` for ${quote.principalName}`}`;
};

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export function formatEmpty(value: any, defaultValue?: string) {
	if (value === "" || value === undefined || value === null) {
		return defaultValue ?? "--";
	}

	return value;
}

export function formatProjectLocation(city: Option<string>, state: Option<string>) {
	const parts = Option.filterNone([city, state]).filter((part) => part.trim() !== "");
	return parts.length > 0 ? parts.join(", ") : formatEmpty(null);
}

export function formatRatio(
	numerator: Option<number>,
	denominator: Option<number>,
	unit: string = "",
): string {
	if (Option.isNone(numerator) || Option.isNone(denominator)) {
		return "--";
	}

	if (denominator === 0) {
		return "--";
	}

	const ratio = numerator / denominator;
	return `${ratio.toFixed(2)}${unit}`;
}

export function formatHtmlString(value: string) {
	return value.replace(/<br\s*\/?>/gi, " ").replace(/<[^>]*>/g, "");
}

export function formatSummaryDateFromEmpty(value: string | null | undefined) {
	if (!value) return value;
	return DateTime.fromISO(value).toLocaleString(DateTime.DATE_FULL);
}

export function formatFromISODate(isoDate?: ISODate | ISODateTime | null | undefined) {
	if (isoDate !== undefined && isoDate !== null) {
		return DateTime.fromISO(isoDate).toLocaleString(DateTime.DATE_SHORT);
	}
}

export const nonEmptyOr = <T extends any>(value: "" | T, replacer: T) => {
	if (value !== "") return value;
	return replacer;
};

export const formatName = (name?: {
	firstName?: string;
	middleInitial?: string | null;
	lastName?: string;
	suffix?: string | null;
}) => {
	if (!name?.firstName || !name?.lastName) return undefined;
	return `${name.firstName}${name.middleInitial ? ` ${name.middleInitial}` : ""} ${name.lastName}${name.suffix ? ` ${name.suffix}` : ""}`;
};

export const formatAnonymousName = (name: {
	firstName?: string;
	middleInitial?: string | null;
	lastName?: string;
	suffix?: string | null;
}) => formatName(name as Required<typeof name>) ?? "[Anonymous]";

export const formatNameOption = (
	name?: {
		firstName: string;
		middleInitial?: string | null;
		lastName: string;
		suffix?: string | null;
	} | null,
) => {
	return (name && formatName(name)) || undefined;
};

export const formatFileName = (unformatted: string) => {
	let formatted = unformatted.replace(/[^a-zA-Z\d\s.]/g, "");
	formatted = formatted.replace(/[\s.]/g, "_");
	return formatted;
};

export const formatDateForCsv = (value: unknown) => {
	if (value instanceof Date) {
		return DateTime.fromJSDate(value).toLocaleString(DateTime.DATE_MED);
	}

	if (typeof value === "string") {
		const parsedDate = DateTime.fromISO(value);
		if (parsedDate.isValid) {
			return parsedDate.toLocaleString(DateTime.DATE_MED);
		}
	}

	return "--";
};
